import moment from "moment-timezone";
import type { StackedColumnBarCategory } from "@plugins/common/types";
import type { Assignment } from "@modules/assignment/assignment-types";
import StringUtils from "@utils/string.utils";

export type AssignmentDate = string | Date | null;
export type AssignmentDisplayStatus =
  "Draft" | "Upcoming" | "Incomplete" | "Overdue" | "Complete" | "Paused";
export type SectionDueStatus = "overdue" | "due-soon" | "";

export interface AssignmentDisplayData {
  startDateTime?: AssignmentDate;
  endDateTime?: AssignmentDate;
  total?: number;
  totalCompleted?: number;
  totalIncomplete?: number;
  paused?: boolean;
  assignmentStatus?: string;
  progressStatus?: string;
}

export interface AssignmentCardData
  extends
    Omit<Partial<Assignment>, "resource" | "studentGroup" | "startDateTime" | "endDateTime">,
    AssignmentDisplayData {
  resource?: Partial<Assignment["resource"]> & { title?: string };
  studentGroup?: Partial<Assignment["studentGroup"]>;
  assignNow?: boolean;
  newAssignment?: boolean;
  pastClass?: boolean;
  hasAccess?: boolean;
  hasParentSubpage?: boolean;
  subpageParentTitle?: string[];
  assigneeGroup?: string;
}

export const ASSIGNMENT_DISPLAY_STATUS_KEYS: Record<AssignmentDisplayStatus, string> = {
  Draft: "media_object_resource_status_draft",
  Upcoming: "assignment_card_label_status_upcoming",
  Incomplete: "assignment_card_label_status_incomplete",
  Overdue: "assignment_card_label_status_overdue",
  Complete: "assignment_card_label_status_complete",
  Paused: "assignment_card_label_status_paused",
};

export interface AssignmentSectionDisplay {
  id?: number | string | null;
  pageNo?: number | null;
  title?: string | null;
  startDateTime?: AssignmentDate;
  endDateTime?: AssignmentDate;
  numCompleted?: number;
  status?: string;
}

export interface AssignmentSectionData {
  sectionDisplayModel?: AssignmentSectionDisplay | null;
  hideSectionCover?: boolean;
  hideSectionActivityNumber?: boolean;
  newSection?: boolean;
  isNewSection?: boolean;
}

export function getAssignmentDateMillis(value?: AssignmentDate): number {
  if (!value) return Number.NaN;
  if (value instanceof Date) return value.getTime();
  return moment.tz(value, ["YYYYMMDDHHmmss", moment.ISO_8601], true, "Asia/Singapore").valueOf();
}

export function formatAssignmentDisplayDate(
  value: AssignmentDate | undefined,
  format: "date" | "time" | "datetime" = "datetime",
): string {
  const timestamp = getAssignmentDateMillis(value);
  if (!Number.isFinite(timestamp)) return "";
  const formats = { date: "DD MMM YYYY", time: "hh:mm A", datetime: "DD MMM YYYY, hh:mm A" };
  return moment(timestamp).tz("Asia/Singapore").format(formats[format]);
}

export function getAssignmentDisplayStatus(
  assignment: AssignmentDisplayData,
  now = Date.now(),
  beforeAssigning = false,
): AssignmentDisplayStatus {
  if (assignment.paused) return "Paused";
  if (beforeAssigning || assignment.assignmentStatus === "Draft") return "Draft";
  if (getAssignmentDateMillis(assignment.startDateTime) > now) return "Upcoming";
  const isComplete =
    assignment.progressStatus != null
      ? assignment.progressStatus === "Completed"
      : assignment.total != null && assignment.total === (assignment.totalCompleted ?? 0);
  if (isComplete) return "Complete";
  if (getAssignmentDateMillis(assignment.endDateTime) < now) return "Overdue";
  return "Incomplete";
}

export function hasAssignmentSections(sectionInfo?: AssignmentSectionData | null): boolean {
  const section = sectionInfo?.sectionDisplayModel;
  return (
    !sectionInfo?.hideSectionCover &&
    !!section &&
    section.id !== 0 &&
    (section.id != null || section.pageNo != null)
  );
}

export function getAssignmentSectionNumber(sectionInfo?: AssignmentSectionData | null): string {
  const pageNo = sectionInfo?.sectionDisplayModel?.pageNo;
  return pageNo && pageNo > 0 && !sectionInfo?.hideSectionActivityNumber
    ? StringUtils.getAlphabeticalLetter(pageNo - 1)
    : "";
}

export function getAssignmentSectionTitle(
  sectionInfo: AssignmentSectionData,
  translate: (key: string, params?: Record<string, unknown>) => string,
): string {
  const number = getAssignmentSectionNumber(sectionInfo);
  return number
    ? translate("section_with_number", { num: number })
    : sectionInfo.sectionDisplayModel?.title || translate("blank_section_cover_label");
}

export function getSectionDueStatus(
  section: AssignmentSectionDisplay,
  total?: number,
  now = Date.now(),
): SectionDueStatus {
  if (section.status?.toLowerCase() === "complete") return "";
  if (total != null && (section.numCompleted ?? 0) >= total) return "";
  const due = getAssignmentDateMillis(section.endDateTime);
  if (due < now) return "overdue";
  if (due <= now + 3 * 24 * 60 * 60 * 1000) return "due-soon";
  return "";
}

export function getSubmissionCategories(completed = 0, total = 0): StackedColumnBarCategory[] {
  const submitted = Math.min(Math.max(completed, 0), Math.max(total, 0));
  return [
    { name: "submitted", cssClass: "completed", quantity: submitted },
    {
      name: "remaining",
      cssClass: "incomplete",
      quantity: Math.max(total - submitted, 0),
    },
  ];
}
